<template>
  <div class="advanced-search-filters">
    <div class="filter-row filter-row--primary">
      <div class="filter-group">
        <label for="advancedSearchFilterDateRange">Date Range</label>
        <Select
          id="advancedSearchFilterDateRange"
          v-model="dateRange"
          class="advanced-search-filter-select"
          :options="dateRangeOptions"
          aria-label="Date range"
          variant="form"
          prefer-placement="top"
        />
      </div>
      <div class="filter-group">
        <label for="advancedSearchFilterCourier">Courier</label>
        <Select
          id="advancedSearchFilterCourier"
          v-model="courier"
          class="advanced-search-filter-select"
          :options="courierOptions"
          aria-label="Courier"
          variant="form"
          prefer-placement="top"
        />
      </div>
      <div class="filter-group">
        <label for="advancedSearchFilterStage">Stage</label>
        <Select
          id="advancedSearchFilterStage"
          v-model="stage"
          class="advanced-search-filter-select"
          :options="stageOptions"
          aria-label="Stage"
          variant="form"
          prefer-placement="top"
        />
      </div>
    </div>
    <div
      v-if="dateRange === 'custom'"
      class="filter-row filter-row--custom-dates custom-date-range"
    >
      <div class="filter-group">
        <label for="advancedSearchFilterDateStart">Start</label>
        <Input
          id="advancedSearchFilterDateStart"
          v-model="dateStart"
          type="date"
          class="advanced-search-filter-input"
          variant="form"
        />
      </div>
      <div class="filter-group">
        <label for="advancedSearchFilterDateEnd">End</label>
        <Input
          id="advancedSearchFilterDateEnd"
          v-model="dateEnd"
          type="date"
          class="advanced-search-filter-input"
          variant="form"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';

import { Input, Select, type SelectOption } from '@/components/ui';
import {
  registerAdvancedSearchFiltersController,
  unregisterAdvancedSearchFiltersController,
  type AdvancedSearchDateRangeFilter,
  type AdvancedSearchFiltersController
} from './advanced-search-filters-controller';

const dateRange = ref('');
const dateStart = ref('');
const dateEnd = ref('');
const courier = ref('');
const stage = ref('');

const dateRangeOptions: SelectOption[] = [
  { value: '', label: 'Any time' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'last_90_days', label: 'Last 90 days' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'custom', label: 'Custom...' }
];

const courierOptions: SelectOption[] = [
  { value: '', label: 'Any courier' },
  { value: 'FedEx', label: 'FedEx' },
  { value: 'UPS', label: 'UPS' },
  { value: 'DHL', label: 'DHL' },
  { value: 'CEVA', label: 'CEVA' },
  { value: 'dayross', label: 'Day & Ross' },
  { value: 'abf', label: 'ABF' },
  { value: 'dbschenker', label: 'DB Schenker' }
];

const stageOptions: SelectOption[] = [
  { value: '', label: 'Any stage' },
  { value: 'active', label: 'Active' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'closed', label: 'Closed' },
  { value: 'exception', label: 'Exception' },
  { value: 'returned', label: 'Returned' }
];

function getDateRangeFilter(): AdvancedSearchDateRangeFilter | undefined {
  if (!dateRange.value) {
    return undefined;
  }

  const now = new Date();
  let startDate: Date | undefined;
  let endDate: Date | undefined;

  switch (dateRange.value) {
    case 'last_7_days':
      startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      endDate = now;
      break;
    case 'last_30_days':
      startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      endDate = now;
      break;
    case 'last_90_days':
      startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      endDate = now;
      break;
    case 'this_month':
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      endDate = now;
      break;
    case 'last_month':
      startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      endDate = new Date(now.getFullYear(), now.getMonth(), 0);
      break;
    case 'custom':
      if (dateStart.value) {
        startDate = new Date(dateStart.value);
      }
      if (dateEnd.value) {
        endDate = new Date(dateEnd.value);
        endDate.setHours(23, 59, 59, 999);
      }
      break;
    default:
      return undefined;
  }

  if (!startDate) {
    return undefined;
  }

  return {
    field: 'date_shipped',
    start: Math.floor(startDate.getTime() / 1000),
    end: endDate ? Math.floor(endDate.getTime() / 1000) : undefined
  };
}

const controller: AdvancedSearchFiltersController = {
  getCourier: () => courier.value,
  getStage: () => stage.value,
  getDateRangeFilter,
  reset: () => {
    dateRange.value = '';
    dateStart.value = '';
    dateEnd.value = '';
    courier.value = '';
    stage.value = '';
  }
};

onMounted(() => {
  registerAdvancedSearchFiltersController(controller);
});

onBeforeUnmount(() => {
  unregisterAdvancedSearchFiltersController(controller);
});
</script>
